import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from '../legal/LegalLayout';
import { TermsIllustration } from '../legal/illustrations';

// ─────────────────────────────────────────────────────────────────────────────
// Nutzungsbedingungen (Terms of Use) for the MainEvents platform, operated by
// Seuytel. Defines who may use the platform and — most importantly — what is
// prohibited. The prohibited-conduct list is derived from the Help Center
// feature set (accounts, ticket purchase/resale, organising events, payments via
// Stripe/PayPal, chat & channels, reporting) so each platform capability has a
// matching rule. Violations can lead to content removal and account deletion.
// ─────────────────────────────────────────────────────────────────────────────

const SUPPORT_EMAIL = 'support@mainevents.seuytel.com';

export default function TermsOfUse() {
    return (
        <LegalLayout
            eyebrow="Nutzungsbedingungen"
            title="Nutzungsbedingungen"
            intro="Die Regeln für die Nutzung der MainEvents-Plattform – wer sie nutzen darf und welche Inhalte und Verhaltensweisen nicht erlaubt sind."
            art={<TermsIllustration />}
        >
            <p>
                Diese Nutzungsbedingungen regeln die Nutzung der Plattform <strong>MainEvents</strong> (App und Website),
                betrieben von <strong>Seuytel</strong>, Walkürenring 4, 38106 Braunschweig. Mit der Registrierung oder
                Nutzung von MainEvents akzeptieren Sie diese Bedingungen. Bitte lesen Sie sie sorgfältig.
            </p>

            <h2>1. Die Plattform</h2>
            <p>
                MainEvents ist eine <strong>Vermittlungsplattform</strong>, auf der Veranstalter Events erstellen und
                Tickets anbieten und Nutzer Events entdecken, Tickets kaufen, weiterverkaufen und an Veranstaltungen
                teilnehmen können. Zahlungen werden über die Dienstleister <strong>Stripe</strong> und{' '}
                <strong>PayPal</strong> abgewickelt. MainEvents ist selbst nicht Veranstalter und nicht Vertragspartner
                des jeweiligen Event-Vertrags zwischen Veranstalter und Teilnehmer.
            </p>

            <h2>2. Konto &amp; Registrierung</h2>
            <ul>
                <li>
                    Sie können sich mit <strong>E-Mail</strong> oder <strong>Google</strong> registrieren und wählen
                    einen Kontotyp (Privat, Veranstalter, Firma, Dienstleister). Ihre Angaben müssen{' '}
                    <strong>wahr, vollständig und aktuell</strong> sein.
                </li>
                <li>
                    Sie sind für die <strong>Geheimhaltung Ihrer Zugangsdaten</strong> und für alle Aktivitäten unter
                    Ihrem Konto verantwortlich.
                </li>
                <li>
                    Die Nutzung ist <strong>Minderjährigen</strong> nur im gesetzlich zulässigen Rahmen und ggf. mit
                    Zustimmung der Erziehungsberechtigten gestattet. Für altersbeschränkte Events gelten zusätzlich die
                    Vorgaben des Veranstalters.
                </li>
                <li>
                    Das Anlegen von Konten mit <strong>falscher Identität</strong>, das Vortäuschen einer fremden Person
                    oder das massenhafte Erstellen von Konten ist untersagt.
                </li>
            </ul>

            <h2>3. Verbotene Inhalte und Verhaltensweisen</h2>
            <p>
                Sie verpflichten sich, keine rechtswidrigen oder unzulässigen Inhalte zu veröffentlichen und die
                Plattform nicht zu missbrauchen. <strong>Untersagt</strong> sind insbesondere:
            </p>
            <ul>
                <li>
                    <strong>Illegale Inhalte:</strong> Inhalte, die gegen geltendes Recht verstoßen – etwa
                    Gewaltdarstellungen, Volksverhetzung, extremistische oder terroristische Inhalte, Darstellungen von
                    Kindesmissbrauch oder andere strafbare Inhalte.
                </li>
                <li>
                    <strong>Hass &amp; Belästigung:</strong> Hassrede, Diskriminierung, Bedrohungen, Mobbing,
                    Belästigung oder das Veröffentlichen privater Daten Dritter (Doxxing).
                </li>
                <li>
                    <strong>Jugendgefährdende &amp; pornografische Inhalte</strong> ohne zulässige Alterskontrolle.
                </li>
                <li>
                    <strong>Betrug &amp; Täuschung:</strong> gefälschte oder nicht existierende Events, betrügerische
                    oder doppelt verkaufte Tickets, irreführende Event-Angaben, Phishing oder sonstige Täuschung von
                    Nutzern.
                </li>
                <li>
                    <strong>Gefährliche oder illegale Veranstaltungen:</strong> Events, die illegale Aktivitäten fördern
                    (z. B. Drogen, Waffen, illegales Glücksspiel) oder die Sicherheit von Personen gefährden.
                </li>
                <li>
                    <strong>Spam &amp; unerwünschte Werbung:</strong> massenhafte Nachrichten, Kettenbriefe, irreführende
                    Links oder das Zweckentfremden von Chats, Gruppen und Kanälen für unerwünschte Werbung.
                </li>
                <li>
                    <strong>Verletzung von Rechten Dritter:</strong> Urheberrechts-, Marken- oder
                    Persönlichkeitsrechtsverletzungen, einschließlich der Nutzung fremder Logos, Bilder oder Marken ohne
                    Erlaubnis.
                </li>
                <li>
                    <strong>Manipulation:</strong> gefälschte Bewertungen, Highlights, Follower oder Reichweite sowie das
                    Manipulieren von Verkaufs- oder Event-Daten.
                </li>
                <li>
                    <strong>Technischer Missbrauch:</strong> automatisiertes Auslesen (Scraping), Reverse Engineering,
                    das Umgehen von Sicherheits- oder Bezahlmechanismen, Schadsoftware oder Angriffe auf die Plattform.
                </li>
                <li>
                    <strong>Datenmissbrauch:</strong> das Sammeln, Speichern oder Weitergeben personenbezogener Daten
                    anderer Nutzer außerhalb des vorgesehenen Zwecks.
                </li>
            </ul>

            <h2>4. Tickets, Kauf &amp; Weiterverkauf</h2>
            <ul>
                <li>
                    Der Kauf, Verkauf und Weiterverkauf von Tickets ist <strong>ausschließlich über die Plattform</strong>{' '}
                    zulässig. Der Weiterverkauf ist nur dort erlaubt, wo der Veranstalter dies zulässt; Übertragung und
                    Abrechnung erfolgen automatisch über MainEvents.
                </li>
                <li>
                    <strong>Untersagt</strong> sind das Anbieten gefälschter Tickets, der gewerbliche
                    Ticket-Schwarzhandel, der Verkauf zu missbräuchlich überhöhten Preisen sowie jeder Verkauf außerhalb
                    der Plattform.
                </li>
                <li>
                    Rückerstattungen richten sich nach den Bedingungen des jeweiligen Events und werden über den
                    Veranstalter und den Zahlungsdienstleister abgewickelt.
                </li>
            </ul>

            <h2>5. Events veranstalten</h2>
            <ul>
                <li>
                    Veranstalter sind allein dafür verantwortlich, dass ihre Events, Inhalte und Tickets{' '}
                    <strong>rechtmäßig</strong> sind und alle erforderlichen Genehmigungen, Steuern und gesetzlichen
                    Pflichten erfüllen.
                </li>
                <li>
                    Event-Angaben (Datum, Ort, Preis, Leistungen) müssen <strong>korrekt</strong> sein. Irreführende oder
                    nicht durchführbare Events sind untersagt.
                </li>
                <li>
                    Veranstalter dürfen die Kommunikationsfunktionen (Chat, Kanäle) nicht für verbotene Inhalte oder
                    Belästigung nutzen.
                </li>
            </ul>

            <h2>6. Zahlungen &amp; Gebühren</h2>
            <p>
                Eventuelle Service- bzw. Plattformgebühren werden vor Kaufabschluss transparent angezeigt. Untersagt sind
                das <strong>Umgehen der Plattformgebühren</strong>, der Missbrauch von Zahlungsmitteln, unberechtigte
                Rückbuchungen (Chargeback-Missbrauch) und jede Form der Geldwäsche. Kartendaten werden ausschließlich bei
                den zertifizierten Dienstleistern Stripe und PayPal verarbeitet.
            </p>

            <h2>7. Geistiges Eigentum</h2>
            <p>
                Die Marke MainEvents, das Logo, die Software und das Design sind geschützt. Inhalte, die Sie einstellen,
                bleiben Ihr Eigentum; Sie räumen MainEvents jedoch das Recht ein, diese Inhalte zum Betrieb und zur
                Darstellung der Plattform zu nutzen. Sie sichern zu, dass Sie über die erforderlichen Rechte verfügen.
            </p>

            <h2>8. Meldungen &amp; Moderation</h2>
            <p>
                Verstößt ein Nutzer oder Event gegen diese Bedingungen, können Sie dies über{' '}
                <Link to="/profil-melden">Profil oder Event melden</Link> mitteilen. Jede Meldung wird vertraulich
                geprüft. Wir behalten uns vor, Inhalte zu entfernen und Maßnahmen zu ergreifen.
            </p>

            <h2>9. Folgen bei Verstößen</h2>
            <p>
                Bei Verstößen gegen diese Nutzungsbedingungen können wir – je nach Schwere – folgende Maßnahmen
                ergreifen:
            </p>
            <ul>
                <li><strong>Entfernen</strong> der betreffenden Inhalte oder Events,</li>
                <li><strong>Verwarnung</strong> oder vorübergehende <strong>Sperrung</strong> des Kontos,</li>
                <li>dauerhafte <strong>Löschung des Kontos</strong> und Ausschluss von der Plattform,</li>
                <li>Weitergabe an die zuständigen Behörden bei strafbaren Handlungen.</li>
            </ul>
            <p className="se-callout">
                <strong>Wichtig:</strong> Das Veröffentlichen illegaler oder unzulässiger Inhalte kann zur{' '}
                <strong>sofortigen Löschung Ihres Kontos</strong> führen.
            </p>

            <h2>10. Kontolöschung</h2>
            <p>
                Sie können Ihr Konto jederzeit selbst löschen (<Link to="/profil-loeschen">Profil löschen</Link>). Bei{' '}
                <strong>aktiven Events mit zahlenden Teilnehmern</strong> ist die Löschung gesperrt, bis diese
                abgeschlossen oder storniert sind. Gesetzliche Aufbewahrungspflichten (z. B. steuer- und handelsrechtlich)
                bleiben unberührt. Einzelheiten zur Verarbeitung Ihrer Daten finden Sie in der{' '}
                <Link to="/datenschutz">Datenschutzerklärung</Link>.
            </p>

            <h2>11. Haftung &amp; Änderungen</h2>
            <p>
                MainEvents stellt die Plattform mit angemessener Sorgfalt bereit, übernimmt jedoch keine Gewähr für die
                Inhalte der Veranstalter oder die Durchführung einzelner Events. Wir können diese Nutzungsbedingungen aus
                sachlichen Gründen anpassen; über wesentliche Änderungen informieren wir rechtzeitig.
            </p>

            <h2>Kontakt</h2>
            <p>
                Fragen zu diesen Nutzungsbedingungen? Schreiben Sie uns an{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
        </LegalLayout>
    );
}
